import { useEffect, useState } from "react";
import { AlertTriangle, ToggleRight } from "lucide-react";
import { platformListFeatureFlags, platformUpdateFeatureFlag } from "../../lib/api";

/**
 * Sprint 1 — Platform Feature Flags panel (admin-only).
 * Only allowlisted foundation flags are shown; unknown keys are rejected
 * server-side.
 */
export default function FeatureFlagsPanel() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await platformListFeatureFlags();
      setFlags(d);
    } catch (e) {
      const body = e?.response?.data;
      setError(body?.message || "Could not load feature flags.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (flag) => {
    setBusy(flag.key);
    setError(null);
    try {
      const updated = await platformUpdateFeatureFlag(flag.key, !flag.enabled);
      setFlags((prev) => prev.map((f) => (f.key === flag.key ? updated : f)));
    } catch (e) {
      const body = e?.response?.data;
      setError(body?.message || "Update failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-feature-flags">
      <header className="mb-7">
        <p className="ed-overline mb-2 flex items-center gap-2">
          <ToggleRight size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
          System
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Feature Flags</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
          Tenant-scoped platform flags. Only the foundation set is exposed. Every change is audited.
        </p>
      </header>

      {error && (
        <div role="alert" data-testid="flags-error" className="mb-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div className="border border-[var(--ed-rule)] rounded-sm bg-white divide-y divide-[var(--ed-rule)]">
        {loading && (
          <p data-testid="flags-loading" className="p-6 text-sm text-[var(--ed-ink-muted)]">Loading…</p>
        )}
        {!loading && flags.length === 0 && (
          <p className="p-6 text-sm text-[var(--ed-ink-muted)]">No flags exposed for this tenant.</p>
        )}
        {flags.map((f) => (
          <div key={f.key} className="p-4 sm:p-5 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm text-[var(--ed-ink)] truncate">{f.key}</p>
              <p className="text-xs text-[var(--ed-ink-muted)] mt-1">{f.description}</p>
              <p className="text-[0.65rem] text-[var(--ed-ink-muted)] mt-1">
                Source: <span className="uppercase tracking-[0.18em]">{f.source}</span>
                {f.updated_at && <> · Updated {new Date(f.updated_at).toLocaleString()}</>}
              </p>
            </div>
            <button
              data-testid={`flag-toggle-${f.key}`}
              onClick={() => toggle(f)}
              disabled={busy === f.key}
              className={`text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 border rounded-sm transition-colors disabled:opacity-40 ${
                f.enabled
                  ? "border-[var(--ed-emerald)] bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)]"
                  : "border-[var(--ed-rule)] text-[var(--ed-ink-muted)] hover:border-[var(--ed-ink)]"
              }`}
              aria-pressed={f.enabled}
            >
              {busy === f.key ? "Saving…" : f.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
